import { IconNames } from '@icon/assets';
import { MarketDataMarket } from '../core/market-data/market-data.model';

/** A single order-book row on either side of the book. */
export interface OrderBookLevel {
	level: number;
	volume: number;
	price: number;
}

/**
 * One contract as the app reads it: the cached `symbolinfo` row joined with its `market-view`
 * figures on `contractId` (see `MarketDataStore`).
 */
export interface Symbol {
	isin: string;
	symbol: string;
	symbolName: string;
	symbolShortName: string;
	contractId: number;
	commodityId: number;
	lastSettlementPrice: number;
	firstTradedPrice: number;
	lastTradedPrice: number;
	/** ISO time of the last trade; null when the contract has not traded. */
	lastTradedPriceTime: string | null;
	/** قیمت پایانی — stays at the last known figure after the close, so never 0 once traded. */
	closingPrice: number;
	/** Last price minus the previous settlement, as the backend sends it — 0 from midnight to the next open. */
	lastPriceChange: number;
	highTradedPrice: number;
	lowTradedPrice: number;
	tradesCount: number;
	tradesVolume: number;
	tradesValue: number;
	openInterests: number;
	openInterestsChanges: number;
	highThreshold: number;
	lowThreshold: number;
	/** Lot size — the minimum dealable quantity. */
	lot: number;
	/** Chunk size the backend splits an oversize order into; not a submit bound. */
	maxOrderSize: number;
	bids: OrderBookLevel[];
	asks: OrderBookLevel[];
	tickSize: number;
	/**
	 * Round-trip commission rate — the buy fee and the sell fee together. Still reported by the
	 * endpoint, but nothing charges it: a trade only ever crosses one side, so
	 * {@link Symbol.buyCommission} and {@link Symbol.sellCommission} are what callers apply.
	 */
	commission: number;
	/**
	 * Per-side commission rates as a **fraction**, not a percentage — the endpoint reports e.g.
	 * `0.0024` for 0.24%. A percentage here would overstate ارزش معامله and جمع کل by 100×.
	 *
	 * Applied as `price * quantity * (1 ± rate)`: the buy rate on a purchase, the sell rate on a
	 * sale. They are the two halves of {@link Symbol.commission} and need no further splitting.
	 */
	buyCommission: number;
	sellCommission: number;
	imeDetailPageUrl: string;
	market: MarketDataMarket;
	marketGroupId: number;
}

/** Shown for commodities that have no icon of their own yet. */
export const DEFAULT_SYMBOL_ICON: IconNames = 'diamond';

/**
 * Commodity icon per `isin`, matched exactly — the endpoint reports each isin in one casing,
 * so there is nothing to fold. Neither the symbol snapshot nor `/orders` returns an icon field, so the
 * icon is derived from the isin — anything not listed here falls back to
 * {@link DEFAULT_SYMBOL_ICON}.
 */
const COMMODITY_ICONS: Readonly<Record<string, IconNames>> = {
	IMEONLINE_DEPOSIT_41: 'stock-gold',
	IMEONLINE_DEPOSIT_46: 'stock-zinc-ingot',
	IMEONLINE_DEPOSIT_43: 'stock-silver-ingot',
	IMEONLINE_DEPOSIT_42: 'stock-gold-coin',
	IMEONLINE_DEPOSIT_44: 'stock-copper',
	IMEONLINE_DEPOSIT_45: 'stock-bitumen',
	IMEONLINE_DEPOSIT_47: 'stock-steel-rebar',
	IMEONLINE_DEPOSIT_48: 'stock-gondola',
	IMEONLINE_DEPOSIT_49: 'stock-kmc',
	saffron: 'saffron',
	IMEONLINE_DEPOSIT_51: 'stock-pistachio',
	IMEONLINE_DEPOSIT_50: 'stock-lead-ingot'
};

/** Picks the commodity icon for a symbol from its `isin`. */
export function symbolIcon(isin: string): IconNames {
	if (!isin) {
		return DEFAULT_SYMBOL_ICON;
	}

	return COMMODITY_ICONS[isin] ?? DEFAULT_SYMBOL_ICON;
}
